import { useCallback, useEffect, useRef } from 'react'
import { useRxDB } from 'rxdb-hooks'
import { OAuth2AuthenticateOptions, OAuth2Client } from '@byteowls/capacitor-oauth2'
import { SecureStoragePlugin, WebStorageAdapter } from '@atroo/capacitor-secure-storage-plugin'
import { useAppActions } from './useAppActions'
import { RxDatabase } from 'rxdb'
import { useObservable, useSubscription } from 'observable-hooks'
import {
  BehaviorSubject,
  defer,
  exhaustMap,
  filter,
  firstValueFrom,
  lastValueFrom,
  retry,
  switchMap,
  take,
} from 'rxjs'
import { useOauth2Web } from './oath2/useOauth2Web'
import { CustomerMetaData } from '@obeta/models/lib/models/Users/UserV2'
import {
  ANDROID_REDIRECT_URL,
  APP_ID,
  AUTH_BASE_URL,
  IOS_REDIRECTURL,
} from '@obeta/utils/lib/config'
import { isPlatform } from '@obeta/utils/lib/isPlatform'
import { useUserDataV2 } from './useUserDataV2'
import { getWebOptions, requestAccessToken } from './oath2/utils'
import { useLocation } from './useHistoryApi'
import { useLoginRedirect } from './useLoginRedirect'
import { WebUtils } from './oath2/web-utils'
import { datadogRum } from '@datadog/browser-rum'
import { fetchPayloadFromToken } from '@obeta/utils/lib/session-based-login-helpers'
import { requestTokenIfExpired } from '@obeta/utils/lib/requestTokenIfExpired'
import {
  clearSessionContext,
  createSessionContext,
  getSessionContext,
  SessionType,
} from '@obeta/utils/lib/session-context'
import { useRouter } from 'next/router'
import { useSessionContextDebugContext } from '../stores/useSessionContextDebugContext'
import Axios from 'axios-observable'
import {
  CompanyToShopDomain,
  ShopDomains,
  ShopDomainToCompany,
  ShopFullDomains,
} from '@obeta/utils/lib/domains'
import { useAppDomain } from './useAppDomain'
import { isUiTarget } from '@obeta/utils/lib/isUiTarget'
import { UserDataResponse } from './oath2/types'
import { Tokens } from '@obeta/models/lib/models/BusinessLayer/AppActions'
import { BroadcastChannel } from 'broadcast-channel'

export const LOGIN_FORWARD_PARAM = 'login_forward_from'

interface MessageTokenReceived {
  action: 'token_received'
  tokens?: Tokens
}

export const channelTokenReceive = new BroadcastChannel<MessageTokenReceived>('token_receive', {
  type: typeof window === 'undefined' ? 'simulate' : 'native',
})

export const options: OAuth2AuthenticateOptions = {
  authorizationBaseUrl: AUTH_BASE_URL + '/oauth2/auth',
  accessTokenEndpoint: AUTH_BASE_URL + '/oauth2/token',
  scope: 'offline openid',
  resourceUrl: AUTH_BASE_URL + '/userinfo',
  logsEnabled: true,
  responseType: 'code',
  pkceEnabled: true,
  web: {
    appId: APP_ID,
    //accessTokenEndpoint: '', // clear the tokenEndpoint as we know that implicit flow gets the accessToken from the authorizationRequest
    //resourceUrl: '',
    redirectUrl: '',
    windowOptions: 'height=600,width=600,left=0,top=0',
    windowTarget: '_self',
  },
  android: {
    appId: APP_ID,
    responseType: 'code', // if you configured a android app in google dev console the value must be "code"
    redirectUrl: ANDROID_REDIRECT_URL, // package name from google dev console
  },
  ios: {
    appId: APP_ID,
    responseType: 'code', // if you configured a ios app in google dev console the value must be "code"
    redirectUrl: IOS_REDIRECTURL, // Bundle ID from google dev console
  },
}

interface CatalogSessionContextOptions {
  sessionType: SessionType
  hookUrl?: string
}

let sessionLoginFinished = false

const saveUserMeta = async (db: RxDatabase, sub: string) => {
  const splits = sub.split('-')
  const companyId = splits.shift()
  const userId = splits.join('-')

  const doc = await db.getLocal<CustomerMetaData>('usermeta')
  // purpose of the isLoggedIn flag is to boradcast this status to all tabs
  // that are opened, it will trigger fetching the latest token from SecureStorage,
  // so all tabs are in the same state
  await doc?.incrementalModify((doc) => {
    doc.isLoggedIn = true
    doc.companyId = companyId || ''
    doc.userId = userId
    return doc
  })
}

const catalogUserSessionIdSubject = new BehaviorSubject<string | undefined>(undefined)
const sessionLoginFinishedSubject = new BehaviorSubject<boolean>(false)

export type PromptForShopRedirectArgs = (
  onDomain: ShopDomains,
  toDomain: ShopDomains
) => Promise<boolean>

export const useLoginListeners = (
  catalogUserSessionId?: string,
  promptForShopRedirect?: PromptForShopRedirectArgs
) => {
  const appActions = useAppActions()
  const { tokens, allTokensExist, metaDataReady } = useUserDataV2()
  const location = useLocation()
  const db = useRxDB()
  const router = useRouter()
  const { pushSessionContextDebugMessage } = useSessionContextDebugContext()
  const domain = useAppDomain()

  catalogUserSessionIdSubject.next(catalogUserSessionId)

  const locationSearch = location.search
  const { redirectBack, clearPath, currentPath } = useLoginRedirect()
  /**
   * In case tokensExist (both access and refresh tokens)
   * We don't need make user to enter password to get these tokens.
   * All we need to do is fetch user meta. After we receive data
   * login process can be completed.
   */
  const userMeta$ = useObservable(
    ($inputs) =>
      $inputs.pipe(
        filter(([tokens, allTokensExist, metaDataReady]) => {
          return Boolean(tokens && allTokensExist && metaDataReady)
        }),
        take(1),
        switchMap(([tokens]) =>
          defer(async () => {
            const sessionContext = getSessionContext()
            if (sessionContext) {
              if ('userId' in sessionContext) {
                pushSessionContextDebugMessage(`save user meta - userId: ${sessionContext.userId}`)
                await saveUserMeta(db, sessionContext.userId as string)
              }
              return
            }
            if (!tokens?.accessToken) {
              throw new Error('no token present')
            }

            let maybeUpdatedAccessToken = tokens?.accessToken
            const updatedTokens = await requestTokenIfExpired(appActions, tokens)
            if (updatedTokens?.accessToken) {
              maybeUpdatedAccessToken = updatedTokens?.accessToken
            }

            const resp = await fetch(AUTH_BASE_URL + '/userinfo', {
              headers: {
                Authorization: `bearer ${maybeUpdatedAccessToken}`,
              },
            })
            const json = await resp.json()
            if (json.error) {
              throw json
            }

            await saveUserMeta(db, json.sub)
          }).pipe(retry(3))
        )
      ),
    [tokens, allTokensExist, metaDataReady]
  )

  useSubscription(
    userMeta$,
    () => {
      //
    },
    async (error) => {
      if (error.status_code === 401) {
        appActions.tokens$.next(null)
        clearSessionContext()
        try {
          await SecureStoragePlugin.clear()
        } catch (err) {
          /*Sentry.addBreadcrumb({
            category: 'auth',
            message: 'Failed to clear secure storage',
          })

          Sentry.captureException(err) */
          datadogRum.addError(err)
          // TODO replace former Sentry-Code with DataDog
        }
      }
    }
  )

  catalogUserSessionIdSubject.subscribe((newCatalogUserSessionId) => {
    // Whenever catalogUserSessionId changes, update it in the observable
    catalogUserSessionId = newCatalogUserSessionId
  })

  sessionLoginFinishedSubject.subscribe((newSessionLoginFinished) => {
    sessionLoginFinished = newSessionLoginFinished
  })

  // Create a BehaviorSubject to manage catalogUserSessionId
  // Subscribe to catalogUserSessionId changes
  const authentication$ = useObservable(
    ($inputs) => {
      return $inputs.pipe(
        exhaustMap(async ([locationSearch]) => {
          if (!isPlatform('web')) {
            // only for web
            return
          }

          let accessToken = ''
          let refreshToken = ''
          let idToken = ''
          let userDataSub = ''
          const sessionId = await firstValueFrom(catalogUserSessionIdSubject)
          if (sessionId && !sessionLoginFinished) {
            pushSessionContextDebugMessage(`sessionId: ${sessionId}`)
            //TODO: Which key is 'cap_sec_auth' exactly? It does not occur in project (anymore?).
            localStorage.removeItem('cap_sec_auth')
            pushSessionContextDebugMessage('fetch tokens by sessionId')
            pushSessionContextDebugMessage(accessToken)

            try {
              const response = await lastValueFrom(
                Axios.post<{ accessToken: string }>(
                  `${process.env.NEXT_PUBLIC_LOGIN_APP_BASE_URL}/api/session/token`,
                  `sessionId=${sessionId}`,
                  {
                    headers: {
                      accept: 'application/json',
                      'cache-control': 'no-cache',
                      'content-type': 'application/x-www-form-urlencoded',
                    },
                  }
                )
              )
              accessToken = response.data.accessToken
              pushSessionContextDebugMessage('tokens retrieved')
              pushSessionContextDebugMessage('parse payload from token')
              const payloadData = fetchPayloadFromToken(accessToken)
              userDataSub = payloadData.userId
              pushSessionContextDebugMessage(`userDataSub ${payloadData.userId}`)
            } catch (error) {
              pushSessionContextDebugMessage(`error on fetching tokens`)
              pushSessionContextDebugMessage(JSON.stringify(error))
            }
            const [, queryParamString] = router.asPath.split('?')
            const base64Options = new URLSearchParams(queryParamString).get('options')
            let catalogSessionContext: CatalogSessionContextOptions = {
              sessionType: 'oci',
            }
            if (base64Options) {
              pushSessionContextDebugMessage(`set session context from incoming options`)
              catalogSessionContext = JSON.parse(atob(base64Options))
            }
            const sessionContext = {
              type: catalogSessionContext.sessionType,
              id: sessionId,
              userId: userDataSub,
              ...catalogSessionContext,
            }
            createSessionContext(sessionContext)
            pushSessionContextDebugMessage(
              `session context created (json: ${JSON.stringify(sessionContext)}`
            )
          } else {
            const webOptions = await getWebOptions(options)
            const params = new URLSearchParams(locationSearch)
            const paramsState = params.get('state')
            const paramsScope = params.get('scope')
            if (!paramsState || !paramsScope) {
              /**
               * Let's assume that parsed search params is not what we need.
               * It's something else. Stop authenctication process.
               */
              return
            }

            if (!webOptions.accessTokenEndpoint) {
              throw new Error('Implicit flow is not implemented')
            }

            if (paramsState !== webOptions.state) {
              if (webOptions.logsEnabled) {
                /* eslint-disable no-console */
                console.log('State from web options: ' + webOptions.state)
                console.log('State returned from provider: ' + paramsState)
                /* eslint-enable no-console */
              }
              throw new Error('ERR_STATES_NOT_MATCH')
            }

            const authorizationCode = params.get('code')
            if (!authorizationCode) {
              throw new Error('ERR_NO_AUTHORIZATION_CODE')
            }

            const accessTokenResponse = await requestAccessToken(webOptions, authorizationCode)
            accessToken = accessTokenResponse['access_token']
            refreshToken = accessTokenResponse['refresh_token']
            idToken = accessTokenResponse['id_token'] //is used for logout requests

            //userinfo and the idToken hold the same information so lets save one roundtrip here
            const idPayload = idToken.split('.')[1]
            const userData: UserDataResponse = JSON.parse(atob(idPayload))

            //todo has the same
            // const userData = await requestUserData(
            //   webOptions.resourceUrl,
            //   accessTokenResponse['access_token'],
            //   webOptions.additionalResourceHeaders,
            //   webOptions.logsEnabled
            // )

            userDataSub = userData.sub

            if (isPlatform('web')) {
              SecureStoragePlugin.setStorageAdapter({
                web: {
                  adapter: userData.isLongLastingSession
                    ? WebStorageAdapter.LocalStorage
                    : WebStorageAdapter.SessionStorage,
                },
              })
            }
          }

          WebUtils.clearVerificationData()

          //set the id token here so that a logout request can properly destroy the cookie for a remember me session
          await SecureStoragePlugin.set({
            key: 'auth',
            value: JSON.stringify({ idToken }),
            accessibility: 'afterFirstUnlock',
          })

          /**
           * the webshop does not allow to login with eldis / hauesler / obeta logins in the other shops for legal reasons
           * for the app this is not a problem, so the crossdomain forward is only considered for the shop not the app
           */
          if (!isUiTarget('app')) {
            //1. check if login matches company
            const currentShopCompanyId = ShopDomainToCompany[domain]
            const loginForCompanyId = userDataSub?.split('-')[0]
            if (!loginForCompanyId) return
            const isSupportDomain =
              [
                'mobile-support.obeta.io',
                'shop-support.obeta.io',
                'staging-shop-support.obeta.io',
              ].indexOf(window.location.host) !== -1
            if (
              !isSupportDomain &&
              loginForCompanyId &&
              currentShopCompanyId !== loginForCompanyId
            ) {
              const redirectTo: ShopDomains = CompanyToShopDomain[loginForCompanyId]

              //2. if not prompt for forward or logout again
              if (promptForShopRedirect) {
                const acceptedForward = await promptForShopRedirect(domain, redirectTo)
                if (!acceptedForward) {
                  //destroy the session in hydra so that a remember me session will not be reusable
                  appActions.logout$.next(true)
                  return
                }

                const redirectUrl = ShopFullDomains[redirectTo]
                if (redirectUrl) {
                  const url = new URL(redirectUrl)
                  url.searchParams.append(LOGIN_FORWARD_PARAM, domain)
                  window.location.replace(url.toString())
                }
                return
              } else {
                //this is an error, the user should not have ended up in this situation
                datadogRum.addError(
                  new Error('user loggedin for wrong domain without forward dialog configuration')
                )
                appActions.logout$.next(true)
                return
              }
            }
          }

          await SecureStoragePlugin.set({
            key: 'auth',
            value: JSON.stringify({ accessToken, refreshToken, idToken }),
            accessibility: 'afterFirstUnlock',
          })

          appActions.tokens$.next({ accessToken, refreshToken, idToken })

          channelTokenReceive.postMessage({
            action: 'token_received',
            tokens: { accessToken, refreshToken, idToken },
          })

          if (sessionId) {
            sessionLoginFinishedSubject.next(true)
          }

          await saveUserMeta(db, userDataSub)

          if (currentPath) {
            redirectBack()
            clearPath()
          }
        })
      )
    },
    [locationSearch]
  )

  useSubscription(
    authentication$,
    (data) => {
      //
    },
    (error) => {
      // Sentry.captureException(error) // TODO replace former Sentry-Code with DataDog
      WebUtils.clearVerificationData()
    }
  )
}

export const useLogin = () => {
  const db = useRxDB()
  const isMounted = useRef(true)
  const appActions = useAppActions()

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  const { authenticate } = useOauth2Web()

  const startLogin = useCallback(async () => {
    localStorage.removeItem('skipOrderingBlockedNotification')
    if (isPlatform('web')) {
      await authenticate(options)
      return
    }

    // this will be executed in native only

    try {
      datadogRum.addAction('login', {
        message: 'starting native login flow',
      })
      const response = await OAuth2Client.authenticate(options)
      const accessToken = response.access_token_response['access_token']
      const refreshToken = response.access_token_response['refresh_token']
      const idToken = response.accessTokenResponse['id_token'] //is used for logout requests

      datadogRum.addAction('login', {
        message: 'received tokens',
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
      })

      await SecureStoragePlugin.set({
        key: 'auth',
        value: JSON.stringify({ accessToken, refreshToken, idToken }),
        accessibility: 'afterFirstUnlock',
      })
      datadogRum.addAction('login', {
        message: 'tokens stored in secure storage',
      })
      appActions.tokens$.next({ accessToken, refreshToken, idToken })
      await saveUserMeta(db, response.sub)
    } catch (error) {
      datadogRum.addError(error)
    }
  }, [appActions, authenticate, db])

  return {
    startLogin,
  }
}
